import { useMsal } from '@azure/msal-react';
import { Outlet, createFileRoute } from '@tanstack/react-router';
import { AppBarTop, Footer, FooterBar, FooterBarLink, IconButton, Spinner, Typography } from '@volvo/vcdk-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import ChangeLanguageModal from '@/components/modals/ChangeLanguageModal';
import SideMenu from '@/components/SideMenu';
import UserMenu from '@/components/UserMenu';
import { AnalyticsContextProvider } from '@/contexts/analytics/AnalyticsContext';
import { FleetAccountProvider } from '@/contexts/fleet-account/FleetAccountProvider';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { UserAuthorizationsProvider } from '@/contexts/user-authorizations/UserAuthorizationsProvider';
import { usePrivateRoute } from '@/hooks/usePrivateRoute';
import { cookiePolicyLink, privacyPolicyLink } from '@/utils/baseLinks';

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedRoot,
  errorComponent: ErrorBoundary,
  notFoundComponent: () => <div>Page Not Found asdas d</div>,
});

function AuthenticatedRoot() {
  return (
    <UserAuthorizationsProvider>
      <FleetAccountProvider>
        <AnalyticsContextProvider>
          <AuthenticatedLayout />
        </AnalyticsContextProvider>
      </FleetAccountProvider>
    </UserAuthorizationsProvider>
  );
}

function AuthenticatedLayout() {
  const { t } = useTranslation();
  const { isLoading: isPrivateRouteLoading } = usePrivateRoute();
  const { instance } = useMsal();
  const { selectedFleetAccount: fleetAccount, isLoading: isFleetAccountLoading } = useFleetAccount();
  const account = instance.getActiveAccount();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserOpen, setIsUserOpen] = useState(false);
  const year = new Date().getFullYear();

  const handleUserDrawerToggle = () => {
    setIsUserOpen((prev) => !prev);
    // Close menu when opening user drawer on mobile
    setIsMenuOpen(false);
  };

  const handleUserDrawerClose = () => {
    setIsUserOpen(false);
  };

  const handleMenuToggle = () => {
    setIsMenuOpen((prev) => !prev);
    // Close user drawer when opening menu on mobile
    setIsUserOpen(false);
  };

  if (isPrivateRouteLoading || isFleetAccountLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col antialiased md:h-screen md:overflow-hidden">
      {/* App Bar - Fixed at top */}
      <AppBarTop serviceName="Volvo Penta Connect">
        <IconButton icon="hamburger-menu" slot="start" onClick={handleMenuToggle} />
        <div slot="end" className="flex items-center gap-6">
          <button
            className="border-subtle flex cursor-pointer flex-col items-end border-l pl-6"
            onClick={handleUserDrawerToggle}
          >
            <Typography variant="caption1" className="leading-none">
              {account?.name || 'User'}
            </Typography>
            <Typography variant="caption2" className="text-subtle leading-none">
              {fleetAccount?.name || 'No fleet selected'}
            </Typography>
          </button>
        </div>
      </AppBarTop>

      {/* Middle section - Flexible, contains sidebar and main content */}
      <div className="relative flex flex-1 md:overflow-hidden">
        <SideMenu isOpen={isMenuOpen} />
        {/* Main Content Area - Scrollable */}
        <main className="bg-base flex max-w-full flex-1 flex-col p-6 sm:overflow-y-auto md:p-9">
          <Outlet />
        </main>
        <UserMenu isOpen={isUserOpen} handleOnToggle={handleUserDrawerClose} />
      </div>

      {/* Footer - Pushed down by content on mobile, fixed at bottom on desktop */}
      <Footer>
        <FooterBar copyright={`© Copyright AB Volvo ${year}`} logotype="volvo-penta-spread-word-mark-stacked">
          <FooterBarLink href="https://www.volvogroup.com/" target="_blank">
            www.volvogroup.com
          </FooterBarLink>
          <ChangeLanguageModal />
          <FooterBarLink href={privacyPolicyLink} target="_blank">
            {t(($) => $.components.footer.privacy_policy)}
          </FooterBarLink>
          <FooterBarLink href={cookiePolicyLink} target="_blank">
            {t(($) => $.components.footer.cookie_policy)}
          </FooterBarLink>
        </FooterBar>
      </Footer>
    </div>
  );
}
